/**
 * Compact quick add (QA T1), rendered by snippets/quick-add-compact.liquid.
 *
 * Shows colour and sizes only; tapping a size adds that variant to the cart.
 * The element moves to <body> while open, like the theme's other drawers:
 * - Mobile: bottom sheet that locks page scroll.
 * - Desktop: popover anchored to the button that opened it, growing upwards
 *   over the card image. It follows the button on scroll and closes on an
 *   outside click.
 *
 * After a successful add the cart drawer opens on desktop (as the product
 * form does); on mobile the sheet confirms the add and closes itself.
 */
if (!customElements.get('quick-add-compact')) {
  const waitForTransition = (element, timeout = 600) =>
    new Promise((resolve) => {
      const done = () => {
        clearTimeout(timer);
        element.removeEventListener('transitionend', onEnd);
        resolve();
      };
      const onEnd = (event) => {
        if (event.target === element) done();
      };
      const timer = setTimeout(done, timeout);
      element.addEventListener('transitionend', onEnd);
    });

  customElements.define(
    'quick-add-compact',
    class QuickAddCompact extends DrawerComponent {
      constructor() {
        super();

        this.panel = this.querySelector('.quick-add__panel');
        this.statusElement = this.querySelector('[data-quick-add-status]');
        this.priceElement = this.querySelector('[data-quick-add-price]');
        this.imageElement = this.querySelector('.quick-add__image');

        try {
          this.variants = JSON.parse(this.querySelector('[data-quick-add-variants]').textContent);
        } catch (error) {
          this.variants = [];
        }

        // Values of every option except the last one (the size, which adds to the cart).
        const initialVariant =
          this.variants.find((variant) => String(variant.id) === this.dataset.variantId) || this.variants[0];
        this.selection = initialVariant ? initialVariant.options.slice(0, -1) : [];

        this.addEventListener('click', this.onClick.bind(this));
        this.onOutsidePointerDown = this.onOutsidePointerDown.bind(this);
        this.onViewportChange = FoxTheme.utils.throttle(this.positionPopover.bind(this));

        // Swipe the bottom sheet down to close it, as its handle suggests.
        this.panel.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: true });
        this.panel.addEventListener('touchmove', this.onTouchMove.bind(this), { passive: true });
        this.panel.addEventListener('touchend', this.onTouchEnd.bind(this));
        this.panel.addEventListener('touchcancel', this.onTouchEnd.bind(this));
      }

      get mode() {
        return this.dataset.mode === 'popover' ? 'popover' : 'sheet';
      }

      get isLockingNeeded() {
        return this.mode === 'sheet';
      }

      // QA T4: the mobile sheet is a drawer and closes the others; the desktop
      // popover can open over the cart drawer's recommendations.
      get closesOtherDrawers() {
        return !window.matchMedia('(min-width: 768px)').matches;
      }

      get requiresBodyAppended() {
        return true;
      }

      get focusElement() {
        return this.panel;
      }

      get anchor() {
        return this.activeElement && this.activeElement.isConnected ? this.activeElement : this.controls[0];
      }

      // Shopify's preview bar (theme preview links only, shoppers never see it)
      // is fixed over the bottom of the screen and would hide the sizes.
      get bottomInset() {
        const previewBar = document.getElementById('PBarNextFrameWrapper');
        return previewBar ? previewBar.offsetHeight : 0;
      }

      prepareToShow() {
        super.prepareToShow();

        // The mode stays the same for the whole open/close cycle, so the scroll
        // lock is undone the same way it was done.
        this.dataset.mode = window.matchMedia('(min-width: 768px)').matches ? 'popover' : 'sheet';
        this.style.setProperty('--quick-add-bottom-inset', `${this.bottomInset}px`);
        clearTimeout(this.closeTimer);
        this.added = false;
        this.classList.remove('is-added');
        this.querySelectorAll('[data-quick-add-size].is-added').forEach((button) => button.classList.remove('is-added'));
        this.setStatus('');
        this.render();
      }

      handleShowTransition() {
        if (this.mode === 'popover') {
          this.positionPopover();
          window.addEventListener('scroll', this.onViewportChange, { capture: true, passive: true });
          window.addEventListener('resize', this.onViewportChange, { passive: true });
        }
        setTimeout(() => this.setAttribute('active', ''), 20);
        return waitForTransition(this.panel);
      }

      handleHideTransition() {
        this.removeAttribute('active');
        window.removeEventListener('scroll', this.onViewportChange, { capture: true });
        window.removeEventListener('resize', this.onViewportChange);
        return waitForTransition(this.panel);
      }

      handleAfterShow() {
        super.handleAfterShow();
        if (this.mode === 'popover') {
          document.addEventListener('pointerdown', this.onOutsidePointerDown);
        }
      }

      handleAfterHide() {
        super.handleAfterHide();
        clearTimeout(this.closeTimer);
        document.removeEventListener('pointerdown', this.onOutsidePointerDown);
        this.panel.style.removeProperty('transform');
      }

      onTouchStart(event) {
        this.dragStartY = null;
        if (this.mode !== 'sheet' || this.busy || this.panel.scrollTop > 0 || event.touches.length !== 1) return;
        this.dragStartY = event.touches[0].clientY;
        this.dragDistance = 0;
      }

      onTouchMove(event) {
        if (this.dragStartY === null || this.dragStartY === undefined) return;
        this.dragDistance = Math.max(0, event.touches[0].clientY - this.dragStartY);
        if (this.dragDistance > 0) {
          this.panel.style.transition = 'none';
          this.panel.style.transform = `translateY(${this.dragDistance}px)`;
        }
      }

      onTouchEnd() {
        if (this.dragStartY === null || this.dragStartY === undefined) return;
        this.dragStartY = null;
        this.panel.style.removeProperty('transition');
        if (this.dragDistance > 80) {
          // Slide out from where the finger left it.
          this.panel.style.transform = 'translateY(100%)';
          this.hide();
        } else {
          this.panel.style.removeProperty('transform');
        }
      }

      positionPopover() {
        if (!this.open || this.mode !== 'popover') return;

        const anchor = this.anchor;
        const rect = anchor && anchor.getBoundingClientRect();
        if (!rect || rect.bottom <= 0 || rect.top >= window.innerHeight) {
          this.hide();
          return;
        }

        // Bottom edge level with the button's bottom edge, centred on it, kept inside the viewport.
        const margin = 8;
        const width = this.panel.offsetWidth;
        const height = this.panel.offsetHeight;
        const bottomLimit = window.innerHeight - this.bottomInset - margin;
        const top = Math.max(margin, Math.min(rect.bottom - height, bottomLimit - height));
        const left = Math.max(margin, Math.min(rect.left + rect.width / 2 - width / 2, window.innerWidth - width - margin));

        this.style.setProperty('--quick-add-top', `${Math.round(top)}px`);
        this.style.setProperty('--quick-add-left', `${Math.round(left)}px`);
      }

      onOutsidePointerDown(event) {
        if (this.contains(event.target)) return;
        if (this.controls.some((control) => control.contains(event.target))) return;
        this.hide();
      }

      onClick(event) {
        if (event.target.closest('[data-quick-add-close]')) {
          event.preventDefault();
          this.hide();
          return;
        }

        const optionButton = event.target.closest('[data-quick-add-option]');
        if (optionButton) {
          if (this.busy || this.added) return;
          this.selection[Number(optionButton.dataset.quickAddOption)] = optionButton.dataset.value;
          this.setStatus('');
          this.render();
          this.positionPopover();
          return;
        }

        const sizeButton = event.target.closest('[data-quick-add-size]');
        if (sizeButton && !sizeButton.disabled) {
          const variant = this.findVariant(sizeButton.dataset.value);
          if (variant && variant.available) this.addToCart(variant, sizeButton);
        }
      }

      findVariant(lastValue) {
        const options = [...this.selection, lastValue];
        return this.variants.find((variant) => variant.options.every((value, index) => value === options[index]));
      }

      matchesSelection(variant) {
        return this.selection.every((value, index) => variant.options[index] === value);
      }

      render() {
        this.querySelectorAll('[data-quick-add-option]').forEach((button) => {
          const index = Number(button.dataset.quickAddOption);
          const value = button.dataset.value;
          button.setAttribute('aria-pressed', this.selection[index] === value ? 'true' : 'false');
          button.classList.toggle(
            'is-unavailable',
            !this.variants.some((variant) => variant.available && variant.options[index] === value)
          );
        });

        this.querySelectorAll('[data-quick-add-selected]').forEach((element) => {
          element.textContent = this.selection[Number(element.dataset.quickAddSelected)] || '';
        });

        this.querySelectorAll('[data-quick-add-size]').forEach((button) => {
          const variant = this.findVariant(button.dataset.value);
          const available = Boolean(variant && variant.available);
          button.disabled = !available;
          button.querySelector('[data-quick-add-sold-out]').hidden = available;
        });

        const matching = this.variants.filter((variant) => this.matchesSelection(variant));
        const shown = matching.find((variant) => variant.available) || matching[0];
        if (shown && this.priceElement) this.renderPrice(shown);

        // Thumbnail follows the selected colour when its variants have their own image.
        const withImage = matching.find((variant) => variant.image);
        if (withImage && this.imageElement && this.imageElement.getAttribute('src') !== withImage.image) {
          this.imageElement.src = withImage.image;
        }
      }

      renderPrice(variant) {
        this.priceElement.textContent = '';
        if (variant.compare) {
          const sale = document.createElement('span');
          sale.className = 'quick-add__price-sale';
          sale.textContent = variant.price;
          const compare = document.createElement('s');
          compare.className = 'quick-add__price-compare';
          compare.textContent = variant.compare;
          this.priceElement.append(sale, ' ', compare);
        } else {
          this.priceElement.textContent = variant.price;
        }
      }

      setStatus(message, isError = false) {
        if (!this.statusElement) return;
        this.statusElement.textContent = message || '';
        this.statusElement.classList.toggle('is-error', Boolean(message) && isError);
      }

      async addToCart(variant, button) {
        if (this.busy || this.added) return;
        this.busy = true;
        this.setStatus('');
        this.panel.setAttribute('aria-busy', 'true');
        button.classList.add('btn--loading');

        // Same request the theme's product form sends, so the cart drawer and
        // cart page sections come back rendered with the response.
        const sections = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
        );

        const formData = new FormData();
        formData.append('id', variant.id);
        formData.append('quantity', variant.min || 1);
        formData.append('sections', sections);
        formData.append('sections_url', window.location.pathname);

        const config = FoxTheme.utils.fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;

        try {
          const response = await fetch(`${FoxTheme.routes.cart_add_url}`, config);
          const parsedState = await response.json();

          if (parsedState.status) {
            FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartError, {
              source: 'quick-add-compact',
              productVariantId: variant.id,
              errors: parsedState.errors || parsedState.description,
              message: parsedState.message,
            });
            this.setStatus(parsedState.description || parsedState.message || FoxTheme.cartStrings.error, true);
            this.positionPopover();
            return;
          }

          const cartJson = await (
            await fetch(`${FoxTheme.routes.cart_url}`, { ...FoxTheme.utils.fetchConfig('json', 'GET') })
          ).json();
          cartJson['sections'] = parsedState['sections'];

          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
          document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: parsedState } }));

          this.onAdded(button);
        } catch (error) {
          console.error(error);
          this.setStatus(FoxTheme.cartStrings.error, true);
        } finally {
          this.busy = false;
          this.panel.removeAttribute('aria-busy');
          button.classList.remove('btn--loading');
        }
      }

      onAdded(button) {
        this.added = true;

        if (document.body.classList.contains('cart-template') || FoxTheme.settings.cartType === 'page') {
          window.location.href = FoxTheme.routes.cart_url;
          return;
        }

        if (this.mode === 'popover') {
          const cartDrawer = document.querySelector('cart-drawer');
          const anchor = this.anchor;
          const hidden = this.hide() || Promise.resolve();
          hidden.then(() => {
            setTimeout(() => {
              if (cartDrawer && !cartDrawer.open) cartDrawer.show(anchor);
            });
          });
          return;
        }

        // Mobile: the cart drawer stays closed; confirm here, then get out of the way.
        this.classList.add('is-added');
        button.classList.add('is-added');
        this.setStatus(this.dataset.addedText);
        this.closeTimer = setTimeout(() => this.hide(), 1500);
      }
    }
  );
}
